import { useEffect } from 'react';
import './App.css';
import { AxiosResponse } from 'axios';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store, { AppDispatch, RootState } from './configureStore';
import { FEInstitution, getConfiguration } from './redux/reducer/configuration';
import { ConfigProvider, Spin, ThemeConfig } from 'antd';
import { BrowserRouter, Route, Routes } from 'react-router';
import SharedLayout from './components/templates/SharedLayout';
import SignInPage from './components/templates/SignInPage';
import PrivateRoute from './components/PrivateRoute';
import AdminPanel from './components/templates/AdminPanel';
import InstitutionConfiguration from './components/templates/InstitutionConfiguration';
import Orchestrator from './components/templates/Orchestrator';
import { MsalProvider } from '@azure/msal-react';
import { internalMsal } from './authInstance';
import useNotification from 'antd/es/notification/useNotification';
import { ERROR_MESSAGE, SUCCESSFUL_MESSAGE } from './redux/reducer/status';
import MagicLinkPage from './components/templates/MagicLinkPage';

const ReduxApp = () => {
  const [notification, contextHolder] = useNotification();
  const dispatch = useDispatch<AppDispatch>();
  const success = useSelector<RootState, string | undefined>(({ status }) => status.successful);
  const error = useSelector<RootState, string | undefined>(({ status }) => status.error);
  const loaded = useSelector<RootState, boolean>(({ status }) => status.success['configuration/GET_CONFIGURATION']);
  const antdConfig = useSelector<RootState, ThemeConfig>(({ configuration }) => configuration.antdConfig);
  const noHeader = useSelector<RootState, boolean>(({ configuration }) => configuration.noHeader);

  function changeFavicon(url: string) {
    const link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (link) {
      link.href = url;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = url;
      document.head.appendChild(newLink);
    }
  }

  function addFont(fontLink: string) {
    const docLink = document.createElement('link');
    docLink.id = 'temp-font-id'
    if (fontLink) {
      docLink.href = fontLink;
      docLink.rel = 'stylesheet';
      document.head.appendChild(docLink);
    }
  }

  useEffect(() => {
    if (success) notification.success({ message: 'Success!', description: success, onClose: () => { dispatch({ type: SUCCESSFUL_MESSAGE }); } });
  }, [success]);

  useEffect(() => {
    if (error) notification.error({ message: 'Something went wrong!', description: error, onClose: () => { dispatch({ type: ERROR_MESSAGE }); } });
  }, [error]);

  useEffect(() => {
    const allowedOrigins = ['http://localhost:3000', 'https://playground.taxwallet.io'];

    const handleEvent = (event: MessageEvent) => {
      if (!allowedOrigins.includes(event.origin)) {
        // console.warn('Received message from unauthorized origin:', event.origin);
        return;
      }

      // Handle the message data here
      // You can dispatch actions or update state based on the message
      if (event.data) {
        // Example: Check for specific message types
        switch (event.data.type) {
          case 'reset-access':
            localStorage.clear();
          // Add your message handling logic here
        }
      }
    };

    window.addEventListener("message", handleEvent);

    // Send a ready message to parent if in iframe
    if (window.parent !== window) {
      try {
        window.parent.postMessage({ type: 'iframe-ready', source: 'orchestrator' }, document.referrer);
      } catch (error) {
        console.error('Error sending message to parent:', error);
      }
    }

    return () => {
      window.removeEventListener("message", handleEvent);
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(getConfiguration({
      input: {
        l: encodeURIComponent(window.location.host),
      },
      onSuccess: (res: AxiosResponse<FEInstitution>) => {
        if (res.status === 200) {
          if (res.data?.customization?.favicon) changeFavicon(res.data.customization.favicon);
          if (res.data?.customization?.fontLink) addFont(res.data.customization.fontLink);
          if (res.data?.customization?.htmlTitle) document.title = res.data?.customization?.htmlTitle;
        }
      }
    }))
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = ``;
    if (antdConfig.token?.colorText) style.textContent += `.tiptap p { color: ${antdConfig.token?.colorText}; }`
    if (antdConfig.token?.colorTextHeading) style.textContent += `
      .tiptap h1, .tiptap h2, .tiptap h3, .tiptap h4, .tiptap h5, .tiptap h6 { color: ${antdConfig.token?.colorTextHeading}; }
    `
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [antdConfig]);


  return loaded ? (
    <ConfigProvider theme={antdConfig}>
      {contextHolder}
      <BrowserRouter>
        <MsalProvider instance={internalMsal}>
          <Routes>
            <Route path="/" element={<SharedLayout noHeader={noHeader} />}>
              <Route index element={<Orchestrator />} />
              <Route path="/privacy-policy" element={<div />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/auth" element={<MagicLinkPage />} />
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <AdminPanel />
                  </PrivateRoute>
                }
              />
              <Route
                path="/institution/:institutionId"
                element={<PrivateRoute><InstitutionConfiguration /></PrivateRoute>}
              />
            </Route>
          </Routes>
        </MsalProvider>
      </BrowserRouter>
    </ConfigProvider>
  ) : <Spin fullscreen />;
};

function App() {
  return (
    <Provider store={store}>
      <ReduxApp />
    </Provider>
  );
}

export default App;
